/**
 * 絵コンテ (storyboard) 本編の「シーン区間」と「モーションの上書き」の純関数。
 * docs/features/brand-cut-motion-graphics-plan.md §10.13 / §10.13.1 (実装契約 A〜D)。
 *
 * 同じアルゴリズム・同じテストベクタの実装が backend にもある (どちらかを変えたら両方変える):
 * - ここ (FE: 動画エディタの mapper・XML 書き出し・シーンカードが共用)
 * - backend/services/scenario/utils/storyboard_spans.py (BE: プレビュー MP4 合成・spans API)
 *
 * ## 用語
 * - 「担当カード」= 絵コンテの動画カード (video_material_id を持つ本編シーン = アンカー)
 * - 「セグメント」= そのカードの担当範囲 (scene_range) に入る本編シーン列
 * - 「区間」= セグメントの動画の中でシーンが占める [in_frame, out_frame) (元動画の fps のフレーム番号・半開区間)
 *
 * ## 規則の要点
 * - 境界は**フレーム番号が正本** (秒をミリ秒に丸めると 30fps の 1 コマ 0.0333 秒が「1 コマ未満」に落ちるため)。
 *   秒は出口でだけ `frame × den / num` で導く (丸めない。表示だけ 2 桁)
 * - 重み = コマ尺 → ナレーション尺 → 0。重みが 1 つも無ければ区間なし (動画 1 本のアイテム・ナレーションは累積)
 * - 比例配分の端数は最大剰余法で整数化し、合計を動画のフレーム数に一致させる
 * - 補正値 (span_override) は版 (base_material_id / card_range / segment_frames) が今と揃うときだけ適用する
 * - モーションの上書き (motion_overlay) は**常に現在の区間**に載せ、生成時との尺差だけで exact / trim / freeze / stale を決める
 */
import { parseSceneRange } from './download/storyboardSegments';
import { OVERLAY_MOTION_FITS, type OverlayMotionFit } from '../videoEditor/schema/overlay';
import type { SceneMotionOverlay, SceneSpanOverride } from './types';

// ── fps (有理数) とフレーム ⇄ 秒 ───────────────────────────────────────────

/** 元動画のフレームレート (ffprobe の r_frame_rate を有理数で)。 */
export interface VideoFps {
  num: number;
  den: number;
}

/** probe できなかったときの既定 (24/1)。 */
export const DEFAULT_FPS: VideoFps = { num: 24, den: 1 };

/** 素材行 `materials.generation_metadata.video_fps = {num, den}` を読む。無ければ 24/1。 */
export function parseVideoFps(generationMetadata: unknown): VideoFps {
  const raw = (generationMetadata as { video_fps?: { num?: unknown; den?: unknown } } | null | undefined)?.video_fps;
  const num = Number(raw?.num);
  const den = Number(raw?.den);
  if (Number.isInteger(num) && Number.isInteger(den) && num > 0 && den > 0) return { num, den };
  return DEFAULT_FPS;
}

/** 素材行に video_fps があるか (無ければ既定に倒す前に span_override の fps を試す)。 */
export function hasVideoFps(generationMetadata: unknown): boolean {
  const raw = (generationMetadata as { video_fps?: unknown } | null | undefined)?.video_fps;
  return parseVideoFps({ video_fps: raw }) !== DEFAULT_FPS;
}

/**
 * probe の fps の優先順位 (BE storyboard_spans_service と同じ):
 * 素材行の `generation_metadata.video_fps` → セグメント内のシーンの span_override.fps (最初に見つかったもの) → 24/1。
 */
export function resolveSegmentFps(
  materialGenerationMetadata: unknown,
  spanOverrides: readonly (SceneSpanOverride | null)[],
): VideoFps {
  if (hasVideoFps(materialGenerationMetadata)) return parseVideoFps(materialGenerationMetadata);
  for (const ov of spanOverrides) {
    if (ov) return ov.fps;
  }
  return DEFAULT_FPS;
}

/** 1 フレームの秒 (den / num)。 */
export function frameSec(fps: VideoFps): number {
  return fps.den / fps.num;
}

/** フレーム番号 → 秒 (出口専用。丸めない)。 */
export function framesToSec(frame: number, fps: VideoFps): number {
  return (frame * fps.den) / fps.num;
}

/** 秒 → フレーム番号 (四捨五入)。 */
export function secToFrames(sec: number, fps: VideoFps): number {
  return Math.round((sec * fps.num) / fps.den);
}

/** セグメント動画の実尺 (probe 値・秒) → フレーム数 `round(sec × num / den)`。不正値は 0。 */
export function segmentFramesFor(durationSec: number, fps: VideoFps): number {
  if (!(durationSec > 0) || !Number.isFinite(durationSec)) return 0;
  return secToFrames(durationSec, fps);
}

// ── 算出規則 (契約 A) ──────────────────────────────────────────────────────

/** セグメント内 1 シーンの時間割りの元 (コマ尺・ナレーション尺・ナレーションの有無)。 */
export interface StoryboardSegmentSceneSpec {
  /** コマ尺 storyboard_duration_seconds (秒)。無ければ 0。 */
  sbDurSec: number;
  /** ナレーション (前面音声) の尺 (秒)。無ければ 0。 */
  narrDurSec: number;
  /** ナレーション (前面音声) の素材 ID。無ければ null。 */
  narrId: string | null;
}

/** シーン区間の重み (コマ尺 → ナレーション尺 → 0 の順)。 */
export function storyboardSpanWeights(specs: readonly StoryboardSegmentSceneSpec[]): number[] {
  return specs.map((spec) => (spec.sbDurSec > 0 ? spec.sbDurSec : spec.narrDurSec > 0 ? spec.narrDurSec : 0));
}

export interface FrameSpan {
  inFrame: number;
  outFrame: number;
}

/**
 * 各シーンの重みをセグメントのフレーム数に比例配分し、累積で [in, out) を返す (契約 A)。
 *
 * - `sum(w) == 0` (重み無し) または `segmentFrames <= 0` なら**空配列** (区間なし)
 * - `w_i × segmentFrames / sum(w)` (**この演算順**。BE と同じ浮動小数の値になる) を**最大剰余法**で整数化する
 *   (合計 = segmentFrames): 各シーンに床値を配り、残りを「小数部の大きい順・同点なら先頭のシーン優先」で
 *   1 フレームずつ足す
 * - 0 フレームの区間はそのまま 0 長で返す (捨てるのは出口 = normalizeSceneSpans)
 */
export function allocateStoryboardSegmentFrames(
  specs: readonly StoryboardSegmentSceneSpec[],
  segmentFrames: number,
): FrameSpan[] {
  const weights = storyboardSpanWeights(specs);
  const total = weights.reduce((sum, w) => sum + w, 0);
  const frames = Math.trunc(segmentFrames || 0);
  if (!(total > 0) || frames <= 0) return [];

  const raw = weights.map((w) => (w * frames) / total);
  const floors = raw.map((r) => Math.floor(r));
  const remainder = frames - floors.reduce((sum, f) => sum + f, 0);
  const order = raw
    .map((r, index) => ({ index, fraction: r - floors[index]! }))
    .sort((a, b) => (b.fraction - a.fraction) || (a.index - b.index))
    .map((entry) => entry.index);
  for (const index of order.slice(0, Math.max(0, remainder))) floors[index]! += 1;

  let cursor = 0;
  return floors.map((f) => {
    const span = { inFrame: cursor, outFrame: cursor + f };
    cursor += f;
    return span;
  });
}

// ── 境界の正本 span_override の適用判定 (契約 B) ───────────────────────────

/** `scenes.generation_metadata.span_override` を読む。形が壊れていれば null。 */
export function parseSpanOverride(generationMetadata: unknown): SceneSpanOverride | null {
  const raw = (generationMetadata as { span_override?: unknown } | null | undefined)?.span_override;
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const inFrame = Number(r.in_frame);
  const outFrame = Number(r.out_frame);
  const segmentFrames = Number(r.segment_frames);
  const range = parseSceneRange({ scene_range: r.card_range });
  const fps = parseVideoFps({ video_fps: r.fps });
  if (
    !Number.isInteger(inFrame) || !Number.isInteger(outFrame) || !Number.isInteger(segmentFrames)
    || typeof r.base_material_id !== 'string' || !r.base_material_id
    || typeof r.card_scene_id !== 'string' || !r.card_scene_id
    || !range
  ) {
    return null;
  }
  return {
    in_frame: inFrame,
    out_frame: outFrame,
    fps,
    base_material_id: r.base_material_id,
    card_scene_id: r.card_scene_id,
    card_range: range,
    segment_frames: segmentFrames,
    saved_at: typeof r.saved_at === 'string' ? r.saved_at : null,
  };
}

/** 担当カード (動画を持つ本編シーン) の今の版情報。 */
export interface StoryboardCard {
  sceneId: string;
  videoMaterialId: string;
  /**
   * 今の担当範囲 = **セグメントの先頭と末尾のシーンの order_number** (BE の segment_card_range と同じ規則。
   * 保存済み scene_range ではない)。span_override.card_range との比較に使う。
   */
  cardRange: { start: number; end: number };
}

export interface SegmentSceneInput {
  sceneId: string;
  spec: StoryboardSegmentSceneSpec;
  spanOverride: SceneSpanOverride | null;
}

export interface ResolvedSceneSpan extends FrameSpan {
  sceneId: string;
  /** span_override が適用された (= 算出値ではない)。隣接の追従だけのシーンは false。 */
  overridden: boolean;
  /** span_override があったが版が合わない / 単調性が崩れて無視した (UI は「絵コンテ動画が変わったので境界を確認してください」)。 */
  boundaryStale: boolean;
}

export interface ResolvedSegmentSpans {
  /** 重み無し (区間なし) のときは空配列。 */
  spans: ResolvedSceneSpan[];
  fps: VideoFps;
  segmentFrames: number;
}

/** 補正値の適用条件 (c): 保存時と今の segment_frames の差の許容 (フレーム)。 */
export const SEGMENT_FRAMES_TOLERANCE = 1;

/** セグメントのシーン列から今の担当範囲を作る (先頭と末尾の order_number)。空なら {0, 0}。 */
export function segmentCardRange(segmentScenes: readonly { order_number: number }[]): { start: number; end: number } {
  if (segmentScenes.length === 0) return { start: 0, end: 0 };
  return { start: segmentScenes[0]!.order_number, end: segmentScenes[segmentScenes.length - 1]!.order_number };
}

/**
 * 算出 (A) を土台に、各シーンの span_override を**3 条件が揃うときだけ**適用する (契約 B):
 * (a) base_material_id == card.videoMaterialId・(b) card_range == 今の担当範囲・
 * (c) |segment_frames − 今の segment_frames| ≤ 1。満たさなければ無視して boundaryStale。
 *
 * 区間列は境界列 b[0..n] (b[i] = シーン i の in・b[i+1] = out) として扱うので、置き換えた境界は隣接シーンにも
 * 反映される (シーン N の out を動かしたらシーン N+1 の in も同じ値。未補正の隣接シーンは算出値から追従)。
 * 先頭の in は常に 0・末尾の out は常に segmentFrames (許容差 ±1 のぶんはここで吸収する)。
 * 同じ境界への食い違う指定・単調性 (in ≤ out・補正シーンは in < out) が崩れる組合せは後勝ちにせず
 * **候補になった補正を全部無視して stale** (算出値に戻す)。
 * BE `storyboard_spans.resolve_segment_spans` と同じアルゴリズム。
 */
export function resolveSegmentSpans(
  card: StoryboardCard,
  scenes: readonly SegmentSceneInput[],
  probe: { fps: VideoFps; segmentFrames: number },
): ResolvedSegmentSpans {
  const { fps } = probe;
  const segmentFrames = Math.max(0, Math.trunc(probe.segmentFrames));
  const base = allocateStoryboardSegmentFrames(scenes.map((s) => s.spec), segmentFrames);
  const n = scenes.length;
  if (base.length === 0 || base.length !== n) return { spans: [], fps, segmentFrames };

  let boundaries: number[] = [base[0]!.inFrame, ...base.map((span) => span.outFrame)];
  const desired = new Map<number, number>();
  const applicable: number[] = [];
  const stale = scenes.map(() => false);
  let conflict = false;
  const clamp = (v: number) => Math.max(0, Math.min(v, segmentFrames));

  scenes.forEach((scene, i) => {
    const ov = scene.spanOverride;
    if (!ov) return;
    const ok =
      ov.base_material_id === card.videoMaterialId
      && ov.card_range.start === card.cardRange.start
      && ov.card_range.end === card.cardRange.end
      && Math.abs(ov.segment_frames - segmentFrames) <= SEGMENT_FRAMES_TOLERANCE
      && Number.isInteger(ov.in_frame) && Number.isInteger(ov.out_frame);
    if (!ok) {
      stale[i] = true;
      return;
    }
    const inF = i === 0 ? 0 : clamp(ov.in_frame);
    const outF = i === n - 1 ? segmentFrames : clamp(ov.out_frame);
    if (inF >= outF) {
      stale[i] = true;
      return;
    }
    for (const [idx, value] of [[i, inF], [i + 1, outF]] as const) {
      if (desired.has(idx) && desired.get(idx) !== value) conflict = true;
      desired.set(idx, value);
    }
    applicable.push(i);
  });

  let applied = false;
  if (applicable.length > 0 && !conflict) {
    const merged = [...boundaries];
    for (const [idx, value] of desired) merged[idx] = value;
    const monotonic = merged.every((v, k) => k === n || v <= merged[k + 1]!);
    const positive = applicable.every((i) => merged[i]! < merged[i + 1]!);
    if (monotonic && positive && merged[0] === 0 && merged[n] === segmentFrames) {
      boundaries = merged;
      applied = true;
    }
  }
  if (applicable.length > 0 && !applied) {
    for (const i of applicable) stale[i] = true;
  }

  return {
    spans: scenes.map((scene, i) => ({
      sceneId: scene.sceneId,
      inFrame: boundaries[i]!,
      outFrame: boundaries[i + 1]!,
      overridden: applied && applicable.includes(i),
      boundaryStale: stale[i]!,
    })),
    fps,
    segmentFrames,
  };
}

// ── モーションの上書き motion_overlay (契約 C) ──────────────────────────────

/** `scenes.generation_metadata.motion_overlay` を読む。形が壊れていれば null。 */
export function parseMotionOverlay(generationMetadata: unknown): SceneMotionOverlay | null {
  const raw = (generationMetadata as { motion_overlay?: unknown } | null | undefined)?.motion_overlay;
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const gs = (r.generated_span && typeof r.generated_span === 'object')
    ? (r.generated_span as Record<string, unknown>)
    : null;
  if (typeof r.material_id !== 'string' || !r.material_id || !gs) return null;
  const inFrame = Number(gs.in_frame);
  const outFrame = Number(gs.out_frame);
  const durationSec = Number(gs.duration_sec);
  const range = parseSceneRange({ scene_range: gs.card_range });
  if (
    !Number.isInteger(inFrame) || !Number.isInteger(outFrame) || !(durationSec > 0)
    || typeof gs.base_material_id !== 'string' || !gs.base_material_id
    || typeof gs.card_scene_id !== 'string' || !gs.card_scene_id
    || !range
  ) {
    return null;
  }
  return {
    material_id: r.material_id,
    job_id: typeof r.job_id === 'string' ? r.job_id : null,
    generated_at: typeof r.generated_at === 'string' ? r.generated_at : null,
    mix_sound: r.mix_sound === true,
    show_telop: r.show_telop === true,
    disabled: r.disabled === true,
    generated_span: {
      base_material_id: gs.base_material_id,
      card_scene_id: gs.card_scene_id,
      card_range: range,
      in_frame: inFrame,
      out_frame: outFrame,
      fps: parseVideoFps({ video_fps: gs.fps }),
      duration_sec: durationSec,
    },
  };
}

/** 上書きの載せ方: exact = そのまま / trim = 末尾を切る / freeze = 末尾フレームで延ばす / stale = 再生成を促す (載せたまま)。 */
export const MOTION_OVERLAY_FITS = OVERLAY_MOTION_FITS;
export type MotionOverlayFit = OverlayMotionFit;

/** モーションは 30fps で描く (生成する尺の切り上げ単位)。 */
export const MOTION_RENDER_FPS = 30;
/** 生成契約の最低尺 (BE build_motion_output / レンダラー validation.ts と同値)。区間がこれ未満なら生成不可。 */
export const MOTION_MIN_DURATION_SEC = 1;
/** |現在の区間秒 − 生成時の尺| がこれ以下なら exact (1 フレーム = 1/30 秒)。 */
export const MOTION_OVERLAY_EXACT_TOLERANCE_SEC = 1 / MOTION_RENDER_FPS;
/** |現在の区間秒 − 生成時の尺| がこれ以下なら trim / freeze で再調整 (超えたら stale)。§10.13 未決 (C) の仮値。 */
export const MOTION_OVERLAY_ADJUST_TOLERANCE_SEC = 0.3;

/**
 * 区間を必ず覆うモーションの生成尺 = `ceil(区間のフレーム数 × den / num × 30) / 30` (最大 1/30 秒長い)。
 * `Math.ceil(frames × den × 30 / num)` の整数演算で求め、浮動小数の誤差で 1/30 秒余計に切り上がらないようにする
 * (BE `motion_output_duration_sec` と同値)。
 */
export function motionOverlayRenderDurationSec(inFrame: number, outFrame: number, fps: VideoFps): number {
  const frames = Math.max(0, Math.trunc(outFrame) - Math.trunc(inFrame));
  return Math.ceil((frames * fps.den * MOTION_RENDER_FPS) / fps.num) / MOTION_RENDER_FPS;
}

export interface ResolvedMotionOverlay {
  materialId: string;
  /** 配置する区間 = **現在の正本の区間** (生成時の区間ではない)。 */
  inFrame: number;
  outFrame: number;
  fit: MotionOverlayFit;
  /** 理由: stale = `base_material_changed` / `duration_changed`・trim / freeze = `duration_adjusted`・exact = null。 */
  reason: 'base_material_changed' | 'duration_changed' | 'duration_adjusted' | null;
  mixSound: boolean;
  /** 上書き区間でも絵コンテのテロップを出す (既定 false = 隠す。合成 3 経路で同じ)。 */
  showTelop: boolean;
  /** 生成時の尺 (generated_span.duration_sec)。 */
  generatedDurationSec: number;
}

/**
 * 上書きの鮮度と載せ方 (契約 C)。`disabled` なら null (上書きしない)。
 * - generated_span.base_material_id ≠ 担当カードの今の動画 → stale
 * - d = 現在の区間秒 − generated_span.duration_sec: |d| ≤ 1/30 → exact・|d| ≤ 0.3 → d<0 trim / d>0 freeze・それ以外 stale
 * - 配置は常に現在の区間 (位置差は見ない。stale でも載せる = 黙って外さない)
 */
export function resolveMotionOverlay(
  overlay: SceneMotionOverlay | null | undefined,
  currentSpan: FrameSpan & { fps: VideoFps },
  card: Pick<StoryboardCard, 'videoMaterialId'>,
): ResolvedMotionOverlay | null {
  if (!overlay || overlay.disabled) return null;
  const common = {
    materialId: overlay.material_id,
    inFrame: currentSpan.inFrame,
    outFrame: currentSpan.outFrame,
    mixSound: overlay.mix_sound === true,
    showTelop: overlay.show_telop === true,
    generatedDurationSec: overlay.generated_span.duration_sec,
  };
  if (overlay.generated_span.base_material_id !== card.videoMaterialId) {
    return { ...common, fit: 'stale', reason: 'base_material_changed' };
  }
  const currentSec = framesToSec(Math.max(0, currentSpan.outFrame - currentSpan.inFrame), currentSpan.fps);
  const d = currentSec - overlay.generated_span.duration_sec;
  if (Math.abs(d) <= MOTION_OVERLAY_EXACT_TOLERANCE_SEC) return { ...common, fit: 'exact', reason: null };
  if (Math.abs(d) <= MOTION_OVERLAY_ADJUST_TOLERANCE_SEC) {
    return { ...common, fit: d < 0 ? 'trim' : 'freeze', reason: 'duration_adjusted' };
  }
  return { ...common, fit: 'stale', reason: 'duration_changed' };
}

// ── 合成経路への載せ方 (契約 D): 区間とタイムラインのアイテム列の交差 ──────────

export interface OverlayItemIntersection<T> {
  item: T;
  /** `items` 内の添字。 */
  index: number;
  /** アイテムの先頭を 0 とした相対秒。`full` なら省略可 (= アイテム全区間)。 */
  startSec: number;
  endSec: number;
  /** 素材 (モーション動画) の何秒から流すか = max(in, item.in) − in。 */
  sourceStartSec: number;
  /** 交差がアイテム全区間に一致する (startSec / endSec を省略してよい)。 */
  full: boolean;
}

const INTERSECT_EPS = 1e-6;

/**
 * 担当動画内の上書き区間 [inSec, outSec) を、同じ動画を `inSec` / `outSec` (素材秒) で切り出したアイテム列と交差させる。
 * 交差が 1 フレーム (元動画の fps) 未満のアイテムには載せない。
 * 通常は区間 = そのシーンのアイテム (境界の正本が同じ) なので 1 アイテム・全区間になるが、区間が作れず
 * 動画 1 本のアイテムのときや、利用者がエディタでアイテムを切り直したときも同じ契約で載る。
 */
export function intersectOverlayWithItems<T extends { inSec: number; outSec: number }>(
  inSec: number,
  outSec: number,
  items: readonly T[],
  fps: VideoFps,
): OverlayItemIntersection<T>[] {
  const minSec = frameSec(fps);
  const result: OverlayItemIntersection<T>[] = [];
  items.forEach((item, index) => {
    const start = Math.max(inSec, item.inSec);
    const end = Math.min(outSec, item.outSec);
    if (end - start < minSec - INTERSECT_EPS) return;
    const full = Math.abs(start - item.inSec) < INTERSECT_EPS && Math.abs(end - item.outSec) < INTERSECT_EPS;
    result.push({
      item,
      index,
      startSec: start - item.inSec,
      endSec: end - item.inSec,
      sourceStartSec: start - inSec,
      full,
    });
  });
  return result;
}
